import * as React from "react";
import { RouteComponentProps } from "react-router-dom";
import { CountyProjectContextProps, withCountyProjectContext } from "@app/projects/CountyProjectContext";
import { SummaryDetailsRouteMatch } from "@app/projects/network-assessment/summary/interfaces";
import { NewActionMenu, NewActionMenuItem } from "@app/shared/action-menu";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { Address } from "@app/shared/data-summaries/Address";
import { BasicRecord } from "@app/shared/data-summaries/basicRecord";
import { ByCounty } from "@app/shared/data-summaries/ByCounty";
import { ByCountyRecord } from "@app/shared/data-summaries/byCountyRecord";
import { ByGeocode } from "@app/shared/data-summaries/ByGeocode";
import { BySpecialty } from "@app/shared/data-summaries/BySpecialty";
import { ByState } from "@app/shared/data-summaries/ByState";
import { SummaryResultNetworkType, SummaryResultScope, SummaryResultType } from "@app/shared/data-summaries/enumerations";
import { NPI } from "@app/shared/data-summaries/NPI";
import { Overall } from "@app/shared/data-summaries/Overall";
import { OverallRecord } from "@app/shared/data-summaries/overallRecord";
import { Specialty } from "@app/shared/data-summaries/Specialty";
import { ViewSelectorItem } from "@app/shared/data-summaries/ViewSelectorItem";
import { ViewSelectorKey, getViewNameFromKey } from "@app/shared/data-summaries/viewSelectorKey";
import { ViewSelectorList } from "@app/shared/data-summaries/ViewSelectorList";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import LoadingSpinner from "@app/shared/LoadingSpinner";

type DataSummariesProps = CountyProjectContextProps & RouteComponentProps<SummaryDetailsRouteMatch> & {
  activeScope: SummaryResultScope;
  defaultView: ViewSelectorKey;
  handleScopeChange: (newScope: DropdownItem<SummaryResultScope>) => void;
  summariesLoading: boolean;
  providers: (networkType: SummaryResultNetworkType, resultType: SummaryResultType, key: string | number) => string;

  addressRecords: BasicRecord[];
  byCountyRecords: ByCountyRecord[];
  byGeocodeRecords: BasicRecord[];
  bySpecialtyRecords: BasicRecord[];
  byStateRecords: BasicRecord[];
  npiRecords: BasicRecord[];
  overallRecords: OverallRecord[];
  specialtyRecords: BasicRecord[];
};

interface DataSummariesState {
  activeView: ViewSelectorKey;
}

class DataSummaries extends React.Component<DataSummariesProps, DataSummariesState> {
  constructor(props: DataSummariesProps) {
    super(props);

    this.state = {
      activeView: this.props.defaultView,
    };
  }

  public componentDidUpdate(prevProps: DataSummariesProps) {
    if (prevProps.match.params.resultType !== this.props.match.params.resultType) {
      this.setState({
        activeView: getViewNameFromKey(this.props.match.params.resultType) || ViewSelectorKey.Overall,
      });
    }
  }

  public render() {
    return (
      <>
        <ViewSelectorList defaultActiveKey={this.props.defaultView} onChange={this.handleViewChange}>
          {(onClick) => ([
            <ViewSelectorItem onClick={onClick} activeView={this.state.activeView} key={ViewSelectorKey.Overall} selectorKey={ViewSelectorKey.Overall}>
              <QesIcon iconName={QesIcons.UsersV1} isDecorative className="align-text-top me-1" /> Overall
            </ViewSelectorItem>,
            <ViewSelectorItem onClick={onClick} activeView={this.state.activeView} key={ViewSelectorKey.BySpecialty} selectorKey={ViewSelectorKey.BySpecialty}>
              <QesIcon iconName={QesIcons.UsersV1} isDecorative className="align-text-top me-1" /> By Specialty
            </ViewSelectorItem>,
            <ViewSelectorItem onClick={onClick} activeView={this.state.activeView} key={ViewSelectorKey.ByCounty} selectorKey={ViewSelectorKey.ByCounty}>
              <QesIcon iconName={QesIcons.UsersV1} isDecorative className="align-text-top me-1" /> By County
            </ViewSelectorItem>,
            <ViewSelectorItem onClick={onClick} activeView={this.state.activeView} key={ViewSelectorKey.ByState} selectorKey={ViewSelectorKey.ByState}>
              <QesIcon iconName={QesIcons.UsersV1} isDecorative className="align-text-top me-1" /> By State
            </ViewSelectorItem>,
            <ViewSelectorItem onClick={onClick} activeView={this.state.activeView} key={ViewSelectorKey.ByGeocode} selectorKey={ViewSelectorKey.ByGeocode}>
              <QesIcon iconName={QesIcons.UsersV1} isDecorative className="align-text-top me-1" /> By Geocode
            </ViewSelectorItem>,
            <ViewSelectorItem onClick={onClick} activeView={this.state.activeView} key={ViewSelectorKey.NPI} selectorKey={ViewSelectorKey.NPI}>
              <QesIcon iconName={QesIcons.Check} isDecorative className="align-text-top me-1" /> NPI
            </ViewSelectorItem>,
            <ViewSelectorItem onClick={onClick} activeView={this.state.activeView} key={ViewSelectorKey.Specialty} selectorKey={ViewSelectorKey.Specialty}>
              <QesIcon iconName={QesIcons.Check} isDecorative className="align-text-top me-1" /> Specialty
            </ViewSelectorItem>,
            <ViewSelectorItem onClick={onClick} activeView={this.state.activeView} key={ViewSelectorKey.Address} selectorKey={ViewSelectorKey.Address}>
              <QesIcon iconName={QesIcons.Check} isDecorative className="align-text-top me-1" /> Address
            </ViewSelectorItem>,
          ])}
        </ViewSelectorList>

        {this.renderScopeToggle()}
        {this.renderActiveView()}
      </>
    );
  }

  private renderScopeToggle(): React.ReactNode {
    const activeText = this.props.activeScope.charAt(0).toUpperCase() + this.props.activeScope.slice(1);
    const dropdownItems: Array<DropdownItem<SummaryResultScope>> = [
      {
        key: SummaryResultScope.Network,
        name: "Network Providers",
      },
      {
        key: SummaryResultScope.Servicing,
        name: "Servicing Providers",
      },
    ];

    return (
      <div className="mt-3 mb-3">
        <NewActionMenu
          buttonClassNames="qes-button-secondary"
          buttonText={`Current Scope: ${activeText} Providers`}
          key={this.props.activeScope}
          loading={this.props.summariesLoading}
          testId="data-summaries__scope-dropdown"
        >
          {dropdownItems.map((item) => {
            const isSelected = this.props.activeScope === item.key;
            return (
              <NewActionMenuItem
                display={item.name}
                isSelected={isSelected}
                key={item.key}
                onClick={() => this.props.handleScopeChange(item)}
                testId={`data-summaries__scope-option--${item.key}`}
              />
            );
          })}
        </NewActionMenu>
      </div>
    );
  }

  private renderActiveView(): React.ReactNode {
    if (this.props.summariesLoading) {
      return <LoadingSpinner />;
    }
    switch (this.state.activeView) {
      case ViewSelectorKey.Overall:
        return <Overall records={this.props.overallRecords} />;
      case ViewSelectorKey.BySpecialty:
        return <BySpecialty providersEndpointFunc={this.props.providers} records={this.props.bySpecialtyRecords} />;
      case ViewSelectorKey.ByCounty:
        return <ByCounty providersEndpointFunc={this.props.providers} records={this.props.byCountyRecords} />;
      case ViewSelectorKey.ByState:
        return <ByState providersEndpointFunc={this.props.providers} records={this.props.byStateRecords} />;
      case ViewSelectorKey.ByGeocode:
        return <ByGeocode providersEndpointFunc={this.props.providers} records={this.props.byGeocodeRecords} />;
      case ViewSelectorKey.NPI:
        return <NPI providersEndpointFunc={this.props.providers} records={this.props.npiRecords} />;
      case ViewSelectorKey.Specialty:
        return <Specialty providersEndpointFunc={this.props.providers} records={this.props.specialtyRecords} />;
      case ViewSelectorKey.Address:
        return <Address providersEndpointFunc={this.props.providers} records={this.props.addressRecords} />;
      default:
        return null;
    }
  }

  private handleViewChange = (key: ViewSelectorKey) => {
    this.setState({ activeView: key });
    const pathArr = this.props.location.pathname.split("/");
    if (this.props.match.path.includes(":resultType")) {
      pathArr.splice(-1, 1);
    }
    pathArr.push(key.replace(/ /g, ""));
    const newPath = pathArr.join("/");
    this.props.history.push(newPath);
  }
}

const WrappedDataSummaries = withCountyProjectContext(DataSummaries);

export { WrappedDataSummaries as DataSummaries };
